import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Chip,
} from '@mui/material';
import { authApi } from '../api/auth';
import { contractorsApi } from '../api/contractors';
import { showSoftSuccess } from '../utils/softSuccess';
import { complexKindLabel, normalizeComplexKind } from '../utils/complexKind';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

const AddClient = () => {
  const [form, setForm] = useState({
    email: '',
    password: '',
    full_name: '',
    tenant: {
      name: '',
      schema_name: '',
      object_kind: 'mdu',
    },
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [tenantSearch, setTenantSearch] = useState('');
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [tenantSaving, setTenantSaving] = useState(false);
  const [tenantEdit, setTenantEdit] = useState({
    name: '',
    company_name: '',
    schema_name: '',
    object_kind: 'mdu',
    usage_start_date: '',
    usage_end_date: '',
    phones: '',
    addresses: '',
    responsible_contacts: [],
    notes: '',
  });
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    full_name: '',
    tenant_name: '',
    tenant_schema_name: '',
  });

  useEffect(() => {
    const loadTenants = async () => {
      setTenantsLoading(true);
      try {
        const data = await contractorsApi.listTenants();
        setTenants(data);
      } catch (err) {
        // keep silent: page can work without list
      } finally {
        setTenantsLoading(false);
      }
    };
    loadTenants();
  }, []);

  const filteredTenants = useMemo(() => {
    if (!tenantSearch) return tenants;
    const q = tenantSearch.toLowerCase();
    return tenants.filter((t) => {
      const name = String(t.name || '').toLowerCase();
      const schema = String(t.schema_name || '').toLowerCase();
      return name.includes(q) || schema.includes(q);
    });
  }, [tenants, tenantSearch]);

  const selectedTenant = useMemo(
    () => tenants.find((t) => String(t.id) === String(selectedTenantId)) || null,
    [tenants, selectedTenantId]
  );

  const openTenantDialog = (tenant) => {
    setSelectedTenantId(String(tenant.id));
    setTenantEdit({
      name: tenant.name || '',
      company_name: tenant.company_name || '',
      schema_name: tenant.schema_name || '',
      object_kind: normalizeComplexKind(tenant.object_kind),
      usage_start_date: tenant.usage_start_date || '',
      usage_end_date: tenant.usage_end_date || '',
      phones: Array.isArray(tenant.phones) ? tenant.phones.join(', ') : '',
      addresses: Array.isArray(tenant.addresses) ? tenant.addresses.join(', ') : '',
      responsible_contacts: Array.isArray(tenant.responsible_contacts)
        ? tenant.responsible_contacts
        : [],
      notes: tenant.notes || '',
    });
    setTenantDialogOpen(true);
  };

  const handleTenantField = (key) => (e) => {
    setTenantEdit((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const addContactRow = () => {
    setTenantEdit((prev) => ({
      ...prev,
      responsible_contacts: [...(prev.responsible_contacts || []), { name: '', role: '', phone: '', email: '' }],
    }));
  };

  const updateContactRow = (index, key) => (e) => {
    const value = e.target.value;
    setTenantEdit((prev) => {
      const next = [...(prev.responsible_contacts || [])];
      next[index] = { ...(next[index] || {}), [key]: value };
      return { ...prev, responsible_contacts: next };
    });
  };

  const removeContactRow = (index) => {
    setTenantEdit((prev) => {
      const next = [...(prev.responsible_contacts || [])];
      next.splice(index, 1);
      return { ...prev, responsible_contacts: next };
    });
  };

  const handleTenantSave = async () => {
    if (!selectedTenantId) return;
    setTenantSaving(true);
    try {
      const contactsRaw = Array.isArray(tenantEdit.responsible_contacts)
        ? tenantEdit.responsible_contacts
        : [];
      const contacts = contactsRaw
        .map((c) => ({
          name: c?.name?.trim() || '',
          role: c?.role?.trim() || '',
          phone: c?.phone?.trim() || '',
          email: c?.email?.trim() || '',
        }))
        .filter((c) => c.name || c.role || c.phone || c.email);
      const payload = {
        name: tenantEdit.name || null,
        company_name: tenantEdit.company_name || null,
        object_kind: normalizeComplexKind(tenantEdit.object_kind),
        usage_start_date: tenantEdit.usage_start_date || null,
        usage_end_date: tenantEdit.usage_end_date || null,
        phones: tenantEdit.phones
          ? tenantEdit.phones.split(',').map((s) => s.trim()).filter(Boolean)
          : null,
        addresses: tenantEdit.addresses
          ? tenantEdit.addresses.split(',').map((s) => s.trim()).filter(Boolean)
          : null,
        responsible_contacts: contacts.length ? contacts : null,
        notes: tenantEdit.notes || null,
      };
      const updated = await contractorsApi.updateTenant(String(selectedTenantId), payload);
      setTenants((prev) => prev.map((t) => (String(t.id) === String(updated.id) ? updated : t)));
      setTenantDialogOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось сохранить карточку');
    } finally {
      setTenantSaving(false);
    }
  };

  const validate = (data) => {
    const next = {
      email: '',
      password: '',
      full_name: '',
      tenant_name: '',
      tenant_schema_name: '',
    };

    // Email
    if (!data.email) {
      next.email = 'Укажите email';
    } else if (!/^\S+@\S+\.\S+$/.test(data.email)) {
      next.email = 'Некорректный email';
    }

    // Password
    if (!data.password) {
      next.password = 'Укажите пароль';
    } else if (data.password.length < 8) {
      next.password = 'Минимум 8 символов';
    }

    // Full name
    // if (!data.full_name) {
    //   next.full_name = 'Укажите полное имя';
    // }

    // Tenant name
    if (!data.tenant?.name) {
      next.tenant_name = 'Укажите название клиента';
    }

    // Schema name
    if (!data.tenant?.schema_name) {
      next.tenant_schema_name = 'Укажите schema name';
    } else if (!/^[a-z0-9_]{1,63}$/.test(data.tenant.schema_name)) {
      next.tenant_schema_name = 'Только строчные латинские буквы, цифры и _ (1–63)';
    }

    return next;
  };

  const validateField = (path, value) => {
    const draft = { ...form };
    if (path.startsWith('tenant.')) {
      const key = path.split('.')[1];
      draft.tenant = { ...draft.tenant, [key]: value };
    } else {
      draft[path] = value;
    }
    const v = validate(draft);
    setErrors(v);
  };

  const handleChange = (path) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      if (path.startsWith('tenant.')) {
        const key = path.split('.')[1];
        return { ...prev, tenant: { ...prev.tenant, [key]: value } };
      }
      return { ...prev, [path]: value };
    });
    validateField(path, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate(form);
    setErrors(v);
    if (Object.values(v).some(Boolean)) {
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await authApi.register({
        ...form,
        tenant: {
          ...form.tenant,
          object_kind: normalizeComplexKind(form.tenant.object_kind),
        },
      });
      setSuccess('Клиент добавлен');
      showSoftSuccess('Клиент добавлен');
      setForm({
        email: '',
        password: '',
        full_name: '',
        tenant: { name: '', schema_name: '', object_kind: 'mdu' },
      });
      setErrors({ email: '', password: '', full_name: '', tenant_name: '', tenant_schema_name: '' });
      try {
        const data = await contractorsApi.listTenants();
        setTenants(data);
      } catch {
        // список необязателен
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось создать клиента');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Добавить клиента</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <Box component="form" onSubmit={handleSubmit} noValidate autoComplete="off">
          {/* hidden autofill bait for Chrome */}
          <input type="text" name="email" autoComplete="username" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />
          <input type="password" name="password" autoComplete="new-password" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                required
                fullWidth
                autoComplete="off"
                name="register_email"
                onFocus={(e) => e.target.removeAttribute('readonly')}
                InputProps={{ readOnly: true }}
                error={Boolean(errors.email)}
                helperText={errors.email}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Пароль"
                type="password"
                value={form.password}
                onChange={handleChange('password')}
                required
                fullWidth
                autoComplete="new-password"
                name="register_password"
                onFocus={(e) => e.target.removeAttribute('readonly')}
                InputProps={{ readOnly: true }}
                error={Boolean(errors.password)}
                helperText={errors.password}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Полное имя"
                value={form.full_name}
                onChange={handleChange('full_name')}
                fullWidth
                // required
                autoComplete="off"
                name="register_fullname"
                onFocus={(e) => e.target.removeAttribute('readonly')}
                InputProps={{ readOnly: true }}
                error={Boolean(errors.full_name)}
                helperText={errors.full_name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Название клиента"
                value={form.tenant.name}
                onChange={handleChange('tenant.name')}
                required
                fullWidth
                autoComplete="off"
                name="tenant_name"
                onFocus={(e) => e.target.removeAttribute('readonly')}
                InputProps={{ readOnly: true }}
                error={Boolean(errors.tenant_name)}
                helperText={errors.tenant_name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Schema name"
                value={form.tenant.schema_name}
                onChange={handleChange('tenant.schema_name')}
                required
                fullWidth
                autoComplete="off"
                name="tenant_schema_name"
                onFocus={(e) => e.target.removeAttribute('readonly')}
                InputProps={{ readOnly: true }}
                error={Boolean(errors.tenant_schema_name)}
                helperText={errors.tenant_schema_name}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl>
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Тип объектов клиента
                </Typography>
                <RadioGroup
                  row
                  value={form.tenant.object_kind || 'mdu'}
                  onChange={handleChange('tenant.object_kind')}
                >
                  <FormControlLabel value="mdu" control={<Radio size="small" />} label="МКД / ЖК" />
                  <FormControlLabel value="settlement" control={<Radio size="small" />} label="Посёлок" />
                </RadioGroup>
                <Typography variant="caption" color="text.secondary">
                  От этого зависят экраны и функции: корпуса и мнемосхема — для МКД, карта домов — для посёлка.
                </Typography>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? 'Сохранение...' : 'Создать клиента'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{'Список клиентов'}</Typography>
          <Typography variant="body2" color="text.secondary">{tenants.length}</Typography>
        </Stack>
        <TextField
          placeholder={'Поиск по названию или schema'}
          value={tenantSearch}
          onChange={(e) => setTenantSearch(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 1.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {tenantsLoading ? (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <List dense sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
            {filteredTenants.map((tenant) => (
              <ListItemButton
                key={tenant.id}
                selected={String(tenant.id) === String(selectedTenantId)}
                onClick={() => openTenantDialog(tenant)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  border: '1px solid rgba(0,0,0,0.06)',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(81, 70, 255, 0.08)',
                    borderColor: 'rgba(81, 70, 255, 0.3)',
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <Typography variant="body1" noWrap>
                        {tenant.name || tenant.schema_name || tenant.id}
                      </Typography>
                      <Chip
                        size="small"
                        label={complexKindLabel(tenant.object_kind || 'mdu')}
                        color={normalizeComplexKind(tenant.object_kind) === 'settlement' ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Stack>
                  }
                  secondary={tenant.schema_name || 'схема не указана'}
                />
              </ListItemButton>
            ))}
            {!filteredTenants.length && (
              <Typography color="text.secondary">{'Нет клиентов'}</Typography>
            )}
          </List>
        )}
      </Paper>

      <Dialog open={tenantDialogOpen} onClose={() => setTenantDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{'\u041a\u0430\u0440\u0442\u043e\u0447\u043a\u0430 \u043a\u043b\u0438\u0435\u043d\u0442\u0430'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {'\u0417\u0434\u0435\u0441\u044c \u043c\u043e\u0436\u043d\u043e \u0434\u043e\u0431\u0430\u0432\u0438\u0442\u044c \u043a\u043e\u043d\u0442\u0430\u043a\u0442\u044b, \u0441\u0440\u043e\u043a\u0438 \u0438 \u043f\u0440\u0438\u043c\u0435\u0447\u0430\u043d\u0438\u044f. \u0412\u0441\u0435 \u043f\u043e\u043b\u044f \u043d\u0435\u043e\u0431\u044f\u0437\u0430\u0442\u0435\u043b\u044c\u043d\u044b.'}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{'\u041e\u0441\u043d\u043e\u0432\u043d\u043e\u0435'}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'\u041d\u0430\u0437\u0432\u0430\u043d\u0438\u0435'}
                    value={tenantEdit.name}
                    onChange={handleTenantField('name')}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'\u041d\u0430\u0437\u0432\u0430\u043d\u0438\u0435 \u043a\u043e\u043c\u043f\u0430\u043d\u0438\u0438'}
                    value={tenantEdit.company_name}
                    onChange={handleTenantField('company_name')}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Schema'}
                    value={tenantEdit.schema_name}
                    disabled
                    helperText={'\u041d\u0435\u043b\u044c\u0437\u044f \u0438\u0437\u043c\u0435\u043d\u0438\u0442\u044c'}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl>
                    <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Тип объектов
                    </Typography>
                    <RadioGroup
                      row
                      value={tenantEdit.object_kind || 'mdu'}
                      onChange={handleTenantField('object_kind')}
                    >
                      <FormControlLabel value="mdu" control={<Radio size="small" />} label="МКД / ЖК" />
                      <FormControlLabel value="settlement" control={<Radio size="small" />} label="Посёлок" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{'\u0421\u0440\u043e\u043a\u0438'}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'\u0414\u0430\u0442\u0430 \u0441\u0442\u0430\u0440\u0442\u0430'}
                    type="date"
                    value={tenantEdit.usage_start_date}
                    onChange={handleTenantField('usage_start_date')}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'\u0414\u0430\u0442\u0430 \u043e\u043a\u043e\u043d\u0447\u0430\u043d\u0438\u044f'}
                    type="date"
                    value={tenantEdit.usage_end_date}
                    onChange={handleTenantField('usage_end_date')}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{'\u041a\u043e\u043d\u0442\u0430\u043a\u0442\u044b'}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'\u0422\u0435\u043b\u0435\u0444\u043e\u043d\u044b'}
                    helperText={'\u0427\u0435\u0440\u0435\u0437 \u0437\u0430\u043f\u044f\u0442\u0443\u044e'}
                    value={tenantEdit.phones}
                    onChange={handleTenantField('phones')}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'\u0410\u0434\u0440\u0435\u0441\u0430'}
                    helperText={'\u0427\u0435\u0440\u0435\u0437 \u0437\u0430\u043f\u044f\u0442\u0443\u044e'}
                    value={tenantEdit.addresses}
                    onChange={handleTenantField('addresses')}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {'\u041e\u0442\u0432\u0435\u0442\u0441\u0442\u0432\u0435\u043d\u043d\u044b\u0435 \u043b\u0438\u0446\u0430'}
                      </Typography>
                      <Button size="small" startIcon={<AddIcon />} onClick={addContactRow}>
                        {'\u0414\u043e\u0431\u0430\u0432\u0438\u0442\u044c \u043a\u043e\u043d\u0442\u0430\u043a\u0442'}
                      </Button>
                    </Stack>
                    {Array.isArray(tenantEdit.responsible_contacts) && tenantEdit.responsible_contacts.length > 0 ? (
                      tenantEdit.responsible_contacts.map((contact, idx) => (
                        <Paper
                          key={idx}
                          variant="outlined"
                          sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'rgba(0,0,0,0.02)' }}
                        >
                          <Stack spacing={1}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {`\u041a\u043e\u043d\u0442\u0430\u043a\u0442 ${idx + 1}`}
                              </Typography>
                              <IconButton size="small" color="error" onClick={() => removeContactRow(idx)}>
                                <DeleteOutlineRoundedIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                            <Divider />
                            <Grid container spacing={1.5}>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  label={'\u0424\u0418\u041e'}
                                  value={contact?.name || ''}
                                  onChange={updateContactRow(idx, 'name')}
                                  fullWidth
                                  size="small"
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  label={'\u0420\u043e\u043b\u044c'}
                                  value={contact?.role || ''}
                                  onChange={updateContactRow(idx, 'role')}
                                  fullWidth
                                  size="small"
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  label={'\u0422\u0435\u043b\u0435\u0444\u043e\u043d'}
                                  value={contact?.phone || ''}
                                  onChange={updateContactRow(idx, 'phone')}
                                  fullWidth
                                  size="small"
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  label={'Email'}
                                  value={contact?.email || ''}
                                  onChange={updateContactRow(idx, 'email')}
                                  fullWidth
                                  size="small"
                                />
                              </Grid>
                            </Grid>
                          </Stack>
                        </Paper>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {'\u041f\u043e\u043a\u0430 \u043d\u0435\u0442 \u043e\u0442\u0432\u0435\u0442\u0441\u0442\u0432\u0435\u043d\u043d\u044b\u0445 \u043b\u0438\u0446. \u041d\u0430\u0436\u043c\u0438\u0442\u0435 \"\u0414\u043e\u0431\u0430\u0432\u0438\u0442\u044c \u043a\u043e\u043d\u0442\u0430\u043a\u0442\".'}
                      </Typography>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{'\u0414\u043e\u043f\u043e\u043b\u043d\u0438\u0442\u0435\u043b\u044c\u043d\u043e'}</Typography>
              <TextField
                label={'\u041f\u0440\u0438\u043c\u0435\u0447\u0430\u043d\u0438\u044f'}
                value={tenantEdit.notes}
                onChange={handleTenantField('notes')}
                fullWidth
                multiline
                minRows={3}
              />
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTenantDialogOpen(false)} disabled={tenantSaving}>{'\u041e\u0442\u043c\u0435\u043d\u0430'}</Button>
          <Button onClick={handleTenantSave} variant="contained" disabled={tenantSaving}>
            {tenantSaving ? '\u0421\u043e\u0445\u0440\u0430\u043d\u0435\u043d\u0438\u0435...' : '\u0421\u043e\u0445\u0440\u0430\u043d\u0438\u0442\u044c'}
          </Button>
        </DialogActions>
      </Dialog>

      </Stack>
    </Box>
  );
};

export default AddClient;

